import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InnerBanner from "../components/InnerBanner";
import Faqs from "../components/Faqs";
import BrandServiceCta from "../components/BrandServiceCta";
import serviceBg from "../assets/hero3.png";
import { getBrandPageData } from "../data/brandPageData";
import "./BrandService.css";

const formatServiceTitle = (serviceSlug) => {
  const words = serviceSlug.split("-");
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getServiceData = (serviceSlug, brand, serviceTitle) => {
  const baseTitle = `${brand} ${serviceTitle}`;
  const brandLower = brand.toLowerCase();

  const commonItems = [
    `${brand} ${serviceTitle} troubleshooting`,
    `${brand} ${serviceTitle} repair and parts replacement`,
    `Doorstep support for ${brand} appliance issues`,
  ];

  switch (serviceSlug) {
    case "washing-machine-service-bangalore":
      return {
        title: `${brand} Washing Machine Service Bangalore`,
        description: `Professional ${brand} washing machine service in Bangalore for front load and top load models with doorstep diagnostics and maintenance.`,
        details: `Our certified ${brand} technicians inspect drum, motor, pump, inlet, and drain systems to keep your washing machine running smoothly. We handle noisy operation, leak repairs, and performance issues quickly and reliably.`,
        serviceItems: [
          `${brand} washing machine preventive maintenance`,
          `${brand} washing machine cleaning and diagnostics`,
          `${brand} door-to-door washing machine service`,
        ],
        keywords: `${brand} washing machine service Bangalore, ${brand} washer repair Bangalore, ${brand} washing machine maintenance`,
      };
    case "washing-machine-repair-bangalore":
      return {
        title: `${brand} Washing Machine Repair Bangalore`,
        description: `Fast ${brand} washing machine repair in Bangalore for all brands and models with genuine spare parts and expert workmanship.`,
        details: `We resolve common faults such as motor failure, drum issues, water leakage, and electrical problems. Our technicians provide quick diagnosis and repair so your washing machine is back in service fast.`,
        serviceItems: [
          `${brand} washing machine fault diagnosis`,
          `${brand} motor and drum repair`,
          `${brand} washing machine parts replacement`,
        ],
        keywords: `${brand} washing machine repair Bangalore, ${brand} washer repair service, ${brand} washing machine service`,
      };
    case "washing-machine-not-spinning":
      return {
        title: `${brand} Washing Machine Not Spinning Repair`,
        description: `Expert ${brand} washing machine not spinning repair in Bangalore for issues caused by belts, motors, drums, and control systems.`,
        details: `Our technicians inspect the drive belt, pulley, clutch, and motor to identify why your washing machine is not spinning. We fix the root cause and restore normal cycles quickly.`,
        serviceItems: [
          `${brand} spin cycle diagnostics`,
          `${brand} motor and belt inspection`,
          `${brand} washing machine not spinning support`,
        ],
        keywords: `${brand} washing machine not spinning, ${brand} washer not spinning repair, ${brand} washing machine spin issue`,
      };
    case "washing-machine-not-working-repair":
      return {
        title: `${brand} Washing Machine Not Working Repair`,
        description: `Reliable ${brand} washing machine not working repair in Bangalore for power, control panel, and startup faults.`,
        details: `We diagnose electrical, panel, and motor faults that prevent the machine from turning on or starting cycles. Our doorstep team restores your washer with genuine parts and professional service.`,
        serviceItems: [
          `${brand} washing machine power fault diagnosis`,
          `${brand} control panel repair`,
          `${brand} washing machine startup service`,
        ],
        keywords: `${brand} washing machine not working repair, ${brand} washer power issue Bangalore, ${brand} washing machine repair`,
      };
    case "washing-machine-water-leak-repair":
      return {
        title: `${brand} Washing Machine Water Leak Repair`,
        description: `Quick ${brand} washing machine water leak repair in Bangalore for inlet, outlet, pipe, and drum leakage problems.`,
        details: `Our team checks hoses, seals, valves, and the drum assembly to locate leaks. We repair damaged parts and stop water loss so your washer works safely at home.`,
        serviceItems: [
          `${brand} washing machine leak inspection`,
          `${brand} hose and seal replacement`,
          `${brand} washing machine water leak repair`,
        ],
        keywords: `${brand} washing machine water leak repair, ${brand} washer leak repair Bangalore, ${brand} washing machine leaking`,
      };
    case "washing-machine-no-power-issue":
      return {
        title: `${brand} Washing Machine No Power Issue`,
        description: `Fast ${brand} washing machine no power issue repair in Bangalore for machines that fail to turn on or lose power intermittently.`,
        details: `We inspect the power supply, fuse, door switches, and control board to restore your machine’s power connection. Our technicians provide safe, expert repair at your doorstep.`,
        serviceItems: [
          `${brand} washing machine power diagnostics`,
          `${brand} electrical fault repair`,
          `${brand} washing machine no power support`,
        ],
        keywords: `${brand} washing machine no power issue, ${brand} washer not powering on, ${brand} washing machine power repair`,
      };
    case "washing-machine-noise-problem":
      return {
        title: `${brand} Washing Machine Noise Problem Repair`,
        description: `Expert ${brand} washing machine noise problem repair in Bangalore for loud vibration, rattling, and drum noise issues.`,
        details: `We diagnose noise caused by bearings, suspension, drum movement, and motor faults. Our service fixes the root cause so your machine runs quietly and efficiently.`,
        serviceItems: [
          `${brand} washing machine noise diagnosis`,
          `${brand} drum and bearing service`,
          `${brand} washing machine vibration repair`,
        ],
        keywords: `${brand} washing machine noise problem, ${brand} noisy washer repair Bangalore, ${brand} washing machine vibration fix`,
      };
    case "washing-machine-drum-repair":
      return {
        title: `${brand} Washing Machine Drum Repair`,
        description: `Professional ${brand} washing machine drum repair in Bangalore for damaged drums, bearings, and imbalance issues.`,
        details: `We inspect the drum, bearings, and paddles to repair or replace damaged parts. Our doorstep service restores stable operation and prevents further wear.`,
        serviceItems: [
          `${brand} drum inspection and repair`,
          `${brand} bearing replacement`,
          `${brand} washing machine balance correction`,
        ],
        keywords: `${brand} washing machine drum repair, ${brand} washer drum replacement, ${brand} washing machine drum service`,
      };
    case "front-load-washing-machine-service-bangalore":
      return {
        title: `${brand} Front Load Washing Machine Service Bangalore`,
        description: `Specialized ${brand} front load washing machine service in Bangalore with expert diagnostics for seal, pump, and motor issues.`,
        details: `Our trained technicians service front load washers for leaks, odor, vibration, and performance problems. We provide cleaning, preventive maintenance, and reliable repairs.`,
        serviceItems: [
          `${brand} front load washer inspection`,
          `${brand} seal and pump servicing`,
          `${brand} front load maintenance`,
        ],
        keywords: `${brand} front load washing machine service Bangalore, ${brand} front load washer service, ${brand} front load repair`,
      };
    case "top-load-washing-machine-service-bangalore":
      return {
        title: `${brand} Top Load Washing Machine Service Bangalore`,
        description: `Trusted ${brand} top load washing machine service in Bangalore for lint, spin, and water flow issues.`,
        details: `We diagnose top load washer problems including drum spin faults, drain blockages, and electrical issues. Our doorstep service restores your machine quickly and efficiently.`,
        serviceItems: [
          `${brand} top load washer diagnostics`,
          `${brand} drain and spin repair`,
          `${brand} top load maintenance`,
        ],
        keywords: `${brand} top load washing machine service Bangalore, ${brand} top load repair, ${brand} washing machine service`,
      };
    case "fridge-service-bangalore":
      return {
        title: `${brand} Fridge Service Bangalore`,
        description: `Professional ${brand} fridge service in Bangalore for cooling, gas refill, and routine maintenance.`,
        details: `Our technicians inspect compressor, thermostat, condenser, and refrigerant systems to keep your fridge cooling reliably. We handle all common refrigerator faults with genuine spare parts.`,
        serviceItems: [
          `${brand} refrigerator diagnostics`,
          `${brand} compressor and cooling service`,
          `${brand} fridge preventive maintenance`,
        ],
        keywords: `${brand} fridge service Bangalore, ${brand} refrigerator service Bangalore, ${brand} fridge repair`,
      };
    case "fridge-not-cooling":
      return {
        title: `${brand} Fridge Not Cooling Repair`,
        description: `Fast ${brand} fridge not cooling repair in Bangalore for compressor, gas, and condenser issues.`,
        details: `We identify cooling faults, refrigerant leaks, compressor failures, and thermostat problems to restore proper refrigerator temperature.`,
        serviceItems: [
          `${brand} fridge cooling diagnosis`,
          `${brand} gas leak detection`,
          `${brand} refrigerator repair`,
        ],
        keywords: `${brand} fridge not cooling, ${brand} refrigerator cooling repair Bangalore, ${brand} fridge service`,
      };
    case "fridge-gas-leakage":
      return {
        title: `${brand} Fridge Gas Leakage Repair`,
        description: `Expert ${brand} fridge gas leakage repair in Bangalore with leak detection and safe refrigerant charging.`,
        details: `Our technicians locate leaks, seal damaged lines, and recharge the refrigerant circuit so your refrigerator cools effectively again.`,
        serviceItems: [
          `${brand} gas leak inspection`,
          `${brand} refrigerant charging`,
          `${brand} fridge gas leak repair`,
        ],
        keywords: `${brand} fridge gas leakage repair, ${brand} refrigerator gas leak Bangalore, ${brand} fridge gas refill`,
      };
    case "fridge-compressor-repair":
      return {
        title: `${brand} Fridge Compressor Repair`,
        description: `Reliable ${brand} fridge compressor repair in Bangalore for broken, noisy, or failing compressors.`,
        details: `We repair or replace compressors, check cooling circuits, and restore fridge performance so your appliance runs efficiently again.`,
        serviceItems: [
          `${brand} compressor diagnostics`,
          `${brand} compressor repair and replacement`,
          `${brand} fridge performance restoration`,
        ],
        keywords: `${brand} fridge compressor repair, ${brand} refrigerator compressor replacement Bangalore, ${brand} compressor service`,
      };
    default:
      return {
        title: baseTitle,
        description: `Customer Supports 24x7 offers ${brand} ${serviceTitle.toLowerCase()} in Bangalore with fast doorstep service and expert technicians.`,
        details: `Our ${brand} ${serviceTitle.toLowerCase()} includes diagnosis, repair, and preventive maintenance for major ${brand} appliances.`,
        serviceItems: commonItems,
        keywords: `${brand} ${serviceTitle} Bangalore, ${brand} appliance repair Bangalore, ${brand} service center Bangalore`,
      };
  }
};

const BrandServiceDetailPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const actualSlug = slug || location.pathname.replace(/^\//, "");

  if (!actualSlug) {
    return null;
  }

  const normalizedSlug = actualSlug.trim().toLowerCase();
  const segments = normalizedSlug.replace(/-bangalore$/, "").split("-");
  const brandKey = segments[0];
  const serviceSlug = segments.slice(1).join("-");

  if (!brandKey || !serviceSlug) {
    return null;
  }

  const page = getBrandPageData(brandKey);

  if (!page) {
    return null;
  }

  const serviceTitle = formatServiceTitle(serviceSlug);
  const serviceData = getServiceData(serviceSlug, page.brand, serviceTitle);
  const title = serviceData.title;
  const description = serviceData.description;
  const details = serviceData.details;
  const serviceItems = serviceData.serviceItems;
  const keywords = serviceData.keywords;

  return (
    <>
      <Helmet>
  <title>{title}</title>

  <meta name="description" content={description} />
  <meta name="keywords" content={keywords} />
  <meta name="robots" content="index, follow" />

  <link
    rel="canonical"
    href={`https://customersupports24x7.in/${actualSlug}`}
  />

  {/* Open Graph */}
  <meta property="og:title" content={title} />
  <meta property="og:description" content={description} />
  <meta
    property="og:image"
    content="https://customersupports24x7.in/customerSupport.png"
  />
  <meta
    property="og:url"
    content={`https://customersupports24x7.in/${actualSlug}`}
  />
  <meta property="og:type" content="website" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={title} />
  <meta name="twitter:description" content={description} />
  <meta
    name="twitter:image"
    content="https://customersupports24x7.in/customerSupport.png"
  />
</Helmet>

      <div className="brand-service-banner">
        <InnerBanner
          title={title}
          bgImage={serviceBg}
          brand={brandKey}
          showActions
        />
      </div>

      <div className={`brand-service-page ${brandKey}-page`}>
        <div className="card">
          <h2>{title}</h2>
          <p style={{ textAlign: "justify" }}>{description}</p>
          <p style={{ textAlign: "justify" }}>{details}</p>
        </div>

        <div className="card brand-info-card">
          <h2>What We Cover</h2>
          <ul className="brand-check-list">
            {serviceItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="card brand-info-card brand-why-card">
          <h2>Why Choose Our {page.brand} Service?</h2>
          <ul className="brand-check-list">
            <li>Experienced technicians for {page.brand} appliances</li>
            <li>Same-day doorstep repair in Bangalore</li>
            <li>Clear diagnosis and honest recommendations</li>
            <li>Service support for major home appliance categories</li>
          </ul>
        </div>
        <BrandServiceCta
          title={`Book ${page.brand} ${serviceTitle} Today`}
          text={`Schedule quick ${page.brand} ${serviceTitle.toLowerCase()} anywhere in Bangalore.`}
        />
      </div>

      <Faqs brand={brandKey} />
    </>
  );
};

export default BrandServiceDetailPage;

